/**
 * Firmio Meet for the demo: one meeting later today, one next week, and two
 * finished ones — one with a transcript and a log entry, one where nobody
 * said anything. Joining is disabled in Test Mode, so the room page only ever
 * shows its lobby here.
 */
import type {
  MeetPublicInfo,
  MeetSegment,
  VideoMeetingDetail,
  VideoMeetingSummary,
} from '../lib/meetApi';
import { isoDateTime } from './company';
import { buildPeople } from './people';

function personName(index: number): string {
  const people = buildPeople();
  return people[index]?.fullName ?? `Coleg ${index + 1}`;
}

const HOST_ID = 'p1';

function at(base: string, minutes: number, seconds = 0): string {
  return new Date(new Date(base).getTime() + (minutes * 60 + seconds) * 1000).toISOString();
}

function seg(
  id: string,
  participantId: string,
  userId: string | null,
  speakerName: string,
  base: string,
  minutes: number,
  seconds: number,
  text: string,
): MeetSegment {
  return {
    id,
    participantId,
    userId,
    speakerName,
    startedAt: at(base, minutes, seconds),
    durationMs: 9_000,
    text,
    createdAt: at(base, minutes, seconds + 10),
  };
}

function demoMeetings(): VideoMeetingDetail[] {
  const laterToday = isoDateTime(0, 16, 0);
  const nextWeek = isoDateTime(5, 10, 30);
  const yesterdayStart = isoDateTime(-1, 11, 2);
  const yesterdayEnd = isoDateTime(-1, 11, 41);
  const lastWeekStart = isoDateTime(-6, 15, 0);
  const lastWeekEnd = isoDateTime(-6, 15, 6);
  const host = personName(0);
  const colleague = personName(1);

  const base = (over: Partial<VideoMeetingSummary>): VideoMeetingSummary => ({
    id: 'vm-0',
    code: 'demo00000000',
    title: 'Întâlnire',
    status: 'SCHEDULED',
    hostId: HOST_ID,
    hostName: host,
    scheduledAt: laterToday,
    durationMinutes: 60,
    clientId: null,
    clientName: null,
    projectId: null,
    projectName: null,
    companyId: null,
    logMeetingId: null,
    logProcessedAt: null,
    startedAt: null,
    endedAt: null,
    durationMs: 0,
    participantCount: 0,
    segmentCount: 0,
    recordingCount: 0,
    joinUrl: 'https://app.firmio.ro/meet/demo00000000',
    attendeeUserIds: [],
    ...over,
  });

  return [
    {
      ...base({
        id: 'vm-1',
        code: 'kxq7mw2nprzd',
        title: 'Ofertă Construct Vest',
        scheduledAt: laterToday,
        durationMinutes: 45,
        clientName: 'Construct Vest SRL',
        joinUrl: 'https://app.firmio.ro/meet/kxq7mw2nprzd',
      }),
      participants: [],
      invites: [
        { id: 'inv-1', userId: 'p2', email: null, name: null },
        { id: 'inv-2', userId: null, email: 'ioana@constructvest.ro', name: 'Ioana Marin' },
      ],
      segments: [],
      canManage: true,
      transcriptionEnabled: true,
    },
    {
      ...base({
        id: 'vm-2',
        code: 'b4tg8cmsuw3e',
        title: 'Planificare sprint',
        scheduledAt: nextWeek,
        durationMinutes: 30,
        joinUrl: 'https://app.firmio.ro/meet/b4tg8cmsuw3e',
      }),
      participants: [],
      invites: [{ id: 'inv-3', userId: 'p2', email: null, name: null }],
      segments: [],
      canManage: true,
      transcriptionEnabled: true,
    },
    {
      ...base({
        id: 'vm-3',
        code: 'n7pdk2vhqx4a',
        title: 'Discovery Cluj Logistic',
        status: 'ENDED',
        scheduledAt: isoDateTime(-1, 11, 0),
        durationMinutes: 45,
        clientName: 'Cluj Logistic SRL',
        logMeetingId: 'm-demo-meet',
        logProcessedAt: at(yesterdayEnd, 3),
        startedAt: yesterdayStart,
        endedAt: yesterdayEnd,
        durationMs: new Date(yesterdayEnd).getTime() - new Date(yesterdayStart).getTime(),
        participantCount: 3,
        segmentCount: 4,
        recordingCount: 0,
        joinUrl: 'https://app.firmio.ro/meet/n7pdk2vhqx4a',
      }),
      participants: [
        {
          id: 'pt-1',
          userId: HOST_ID,
          displayName: host,
          guest: false,
          admission: 'ADMITTED',
          joinedAt: yesterdayStart,
          admittedAt: yesterdayStart,
          leftAt: yesterdayEnd,
        },
        {
          id: 'pt-2',
          userId: 'p2',
          displayName: colleague,
          guest: false,
          admission: 'ADMITTED',
          joinedAt: at(yesterdayStart, 1),
          admittedAt: at(yesterdayStart, 1),
          leftAt: yesterdayEnd,
        },
        {
          id: 'pt-3',
          userId: null,
          displayName: 'Radu Ionescu',
          guest: true,
          admission: 'ADMITTED',
          joinedAt: at(yesterdayStart, 1, 30),
          admittedAt: at(yesterdayStart, 2),
          leftAt: yesterdayEnd,
        },
      ],
      invites: [{ id: 'inv-4', userId: null, email: 'radu@clujlogistic.ro', name: 'Radu Ionescu' }],
      segments: [
        seg(
          'ms-1',
          'pt-1',
          HOST_ID,
          host,
          yesterdayStart,
          2,
          20,
          'Mulțumim că ați acceptat întâlnirea. Vrem să înțelegem cum gestionați azi comenzile.',
        ),
        seg(
          'ms-2',
          'pt-3',
          null,
          'Radu Ionescu',
          yesterdayStart,
          2,
          45,
          'Avem trei depozite și totul merge pe Excel. Pierdem cam o zi pe săptămână cu reconcilierea.',
        ),
        seg(
          'ms-3',
          'pt-2',
          'p2',
          colleague,
          yesterdayStart,
          4,
          10,
          'Putem începe cu un modul de comenzi și livrări, integrat cu banca, în șase săptămâni.',
        ),
        seg(
          'ms-4',
          'pt-1',
          HOST_ID,
          host,
          yesterdayStart,
          5,
          0,
          'Perfect. Vă trimitem oferta până joi, cu două variante de preț.',
        ),
      ],
      canManage: true,
      transcriptionEnabled: true,
    },
    {
      ...base({
        id: 'vm-4',
        code: 'w2rhy6ndm8jq',
        title: 'Sync rapid',
        status: 'ENDED',
        scheduledAt: lastWeekStart,
        durationMinutes: 15,
        logProcessedAt: at(lastWeekEnd, 3),
        startedAt: lastWeekStart,
        endedAt: lastWeekEnd,
        durationMs: new Date(lastWeekEnd).getTime() - new Date(lastWeekStart).getTime(),
        participantCount: 2,
        segmentCount: 0,
        recordingCount: 0,
        joinUrl: 'https://app.firmio.ro/meet/w2rhy6ndm8jq',
      }),
      participants: [
        {
          id: 'pt-4',
          userId: HOST_ID,
          displayName: host,
          guest: false,
          admission: 'ADMITTED',
          joinedAt: lastWeekStart,
          admittedAt: lastWeekStart,
          leftAt: lastWeekEnd,
        },
        {
          id: 'pt-5',
          userId: 'p2',
          displayName: colleague,
          guest: false,
          admission: 'ADMITTED',
          joinedAt: lastWeekStart,
          admittedAt: lastWeekStart,
          leftAt: lastWeekEnd,
        },
      ],
      invites: [],
      segments: [],
      canManage: true,
      transcriptionEnabled: true,
    },
  ];
}

export function buildVideoMeetings(input: {
  tab: string | null;
  from: string | null;
  to: string | null;
}): VideoMeetingSummary[] {
  return demoMeetings()
    .filter((m) => {
      if (input.tab === 'upcoming') return m.status === 'SCHEDULED' || m.status === 'LIVE';
      if (input.tab === 'past') return m.status === 'ENDED' || m.status === 'CANCELLED';
      return true;
    })
    .filter((m) => {
      if (input.from && m.scheduledAt < input.from) return false;
      if (input.to && m.scheduledAt > input.to) return false;
      return true;
    })
    .map(
      ({
        participants: _p,
        invites: _i,
        segments: _s,
        canManage: _c,
        transcriptionEnabled: _t,
        ...summary
      }) => summary,
    );
}

export function buildVideoMeeting(id: string): VideoMeetingDetail | null {
  return demoMeetings().find((m) => m.id === id) ?? null;
}

export function buildMeetPublic(code: string): MeetPublicInfo | null {
  const m = demoMeetings().find((x) => x.code === code);
  if (!m) return null;
  return {
    id: m.id,
    code: m.code,
    title: m.title,
    status: m.status,
    hostName: m.hostName,
    scheduledAt: m.scheduledAt,
    durationMinutes: m.durationMinutes,
    startedAt: m.startedAt,
    endedAt: m.endedAt,
    joinable: false,
    opensAt: m.scheduledAt,
    participantCount: 0,
    transcriptionEnabled: true,
    livekitConfigured: false,
    me: null,
  };
}
